/**
 * CR168: Hook for managing flyer bucket sources
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { FlyerBucketSource } from "@/lib/flyer-bucket/types";

export function useFlyerBucketSources() {
  const { toast } = useToast();
  const [sources, setSources] = useState<FlyerBucketSource[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSources = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("flyer_bucket_sources")
        .select("*")
        .order("name");

      if (error) throw error;
      setSources((data || []) as FlyerBucketSource[]);
    } catch (err) {
      console.error("Failed to fetch flyer bucket sources:", err);
      toast({
        title: "Error",
        description: "Failed to load flyer bucket sources",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const createSource = async (source: Omit<FlyerBucketSource, "id" | "created_at" | "updated_at">): Promise<string | null> => {
    try {
      const { s0_flyer_fetch_config, ...rest } = source;
      const { data, error } = await supabase
        .from("flyer_bucket_sources")
        .insert({ ...rest, s0_flyer_fetch_config: s0_flyer_fetch_config as any })
        .select("id")
        .single();

      if (error) throw error;
      
      toast({
        title: "Created",
        description: `Bucket source "${source.name}" created successfully`,
      });
      
      await fetchSources();
      return data.id;
    } catch (err: any) {
      console.error("Failed to create bucket source:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to create bucket source",
        variant: "destructive",
      });
      return null;
    }
  };

  const updateSource = async (id: string, updates: Partial<FlyerBucketSource>): Promise<boolean> => {
    try {
      const { s0_flyer_fetch_config, ...rest } = updates;
      const payload: Record<string, unknown> = { ...rest, updated_at: new Date().toISOString() };
      if (s0_flyer_fetch_config !== undefined) {
        payload.s0_flyer_fetch_config = s0_flyer_fetch_config;
      }
      const { error } = await supabase
        .from("flyer_bucket_sources")
        .update(payload as any)
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Updated",
        description: "Bucket source updated successfully",
      });
      
      await fetchSources();
      return true;
    } catch (err: any) {
      console.error("Failed to update bucket source:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to update bucket source",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteSource = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("flyer_bucket_sources")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Deleted",
        description: "Bucket source deleted successfully",
      });
      
      await fetchSources();
      return true;
    } catch (err: any) {
      console.error("Failed to delete bucket source:", err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete bucket source",
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  return {
    sources,
    loading,
    fetchSources,
    createSource,
    updateSource,
    deleteSource,
  };
}
